import type { LoanApplicationData } from "../../hooks/useNbiForm";
import { normalize, parseNameParts, titleCase } from "./common";
import type { IdentityConfirmation, SetFieldValueCallback } from "./models";
import { extractReferencesFromMultiple } from "./parserUtils";

const mapBackendIdentityTypeToFrontend = (identityType: IdentityConfirmation["identityType"]): string => {
    switch (identityType) {
        case "drivers_license":
            return "Driver's License";
        case "passport":
            return "Passport";
        case "birth_certificate":
            return "Birth Certificate";
        case "national_ID_card":
            return "National ID Card";
        case "others":
        default:
            return "Driver's License"; // Default fallback
    }
};

const mapBackendStateToFrontend = (state: string | undefined, defaultState = "NSW"): string => {
    // apparently state is a dropdown selector with a few options, so it needs to match there.
    if (!state) return defaultState;

    const normalized = state.toUpperCase().trim().replace(/[.\s]/g, "");

    const validAbbr = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];
    if (validAbbr.includes(normalized)) return normalized;

    const statePatterns = [
        { pattern: /NEW.*SOUTH.*WALES|NEWSOUTHWALES/i, abbr: "NSW" },
        { pattern: /VICTORIA/i, abbr: "VIC" },
        { pattern: /QUEENSLAND/i, abbr: "QLD" },
        { pattern: /WESTERN.*AUSTRALIA|WESTERNAUSTRALIA/i, abbr: "WA" },
        { pattern: /SOUTH.*AUSTRALIA|SOUTHAUSTRALIA/i, abbr: "SA" },
        { pattern: /TASMANIA/i, abbr: "TAS" },
        { pattern: /NORTHERN.*TERRITORY|NORTHERNTERRITORY/i, abbr: "NT" },
        { pattern: /AUSTRALIAN.*CAPITAL|ACT/i, abbr: "ACT" },
    ];

    for (const { pattern, abbr } of statePatterns) {
        if (pattern.test(state)) return abbr;
    }

    return state;
};

const mapBackendCountryToFrontend = (country: string | undefined, defaultCountry = "Australia"): string => {
    if (!country) return defaultCountry;
    const normalized = country.trim();
    if (normalized.toUpperCase() === "AUSTRALIA") {
        return "Australia";
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

interface IdentityRegionDefaults {
    citizenship: string;
    state: string;
    country: string;
}

const handleIdentityDocument = (
    data: IdentityConfirmation,
    currentFormData: LoanApplicationData,
    setFieldValue: SetFieldValueCallback,
    regionDefaults: IdentityRegionDefaults = { citizenship: "Australian", state: "NSW", country: "Australia" }
): string => {
    const existingApplicants = Array.isArray(currentFormData.applicants) ? currentFormData.applicants : [];
    const findMatchingApplicantIndex = (): number => {
        const id = data.id;
        const idVal = normalize(data.idValue);
        const given = normalize(parseNameParts(data.givenName));
        const family = normalize(data.familyName);
        return existingApplicants.findIndex((a) => {
            const idMatch = id && a.id === id.toString();
            const idValMatch = idVal && normalize(a.idValue) === idVal;
            const nameMatch =
                given &&
                family &&
                normalize(parseNameParts(a.givenName)) === given &&
                normalize(a.familyName) === family;
            return idMatch || idValMatch || nameMatch;
        });
    };

    const idx = findMatchingApplicantIndex();
    const applicantId =
        idx >= 0
            ? existingApplicants[idx].id
            : data.id?.toString() || `app_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    if (idx >= 0 && data.isIncomplete) {
        // The incomplete record carries the backend entity ID that employments, incomes,
        // and expenses all reference. Sync the stored applicant to use this entity ID
        // so downstream lookups succeed.
        const entityId = data.id?.toString();
        if (entityId && entityId !== existingApplicants[idx].id) {
            const updatedApplicants = [...existingApplicants];
            updatedApplicants[idx] = { ...updatedApplicants[idx], id: entityId };
            setFieldValue("applicants", updatedApplicants);
            return entityId;
        }
        return applicantId;
    }

    const frontendIdentityType = data.identityType ? mapBackendIdentityTypeToFrontend(data.identityType) : undefined;

    const references = extractReferencesFromMultiple(
        [data, data.address],
        existingApplicants[idx]?.references
    );

    const nextApplicant = {
        id: applicantId,
        givenName: titleCase(data.givenName || existingApplicants[idx]?.givenName || ""),
        familyName: titleCase(data.familyName || existingApplicants[idx]?.familyName || ""),
        dateOfBirth: data.dateOfBirth || existingApplicants[idx]?.dateOfBirth || "",
        citizenship: data.isForeignOrNonResident ? "Other" : existingApplicants[idx]?.citizenship || regionDefaults.citizenship,
        addressLine1: data.address?.addressLine || existingApplicants[idx]?.addressLine1 || "",
        suburb: data.address?.suburb || existingApplicants[idx]?.suburb || "",
        city: data.address?.city || existingApplicants[idx]?.city || "",
        state: mapBackendStateToFrontend(data.address?.state, regionDefaults.state) || existingApplicants[idx]?.state || regionDefaults.state,
        postCode: data.address?.postCode || existingApplicants[idx]?.postCode || "",
        country: mapBackendCountryToFrontend(data.address?.country, regionDefaults.country) || existingApplicants[idx]?.country || regionDefaults.country,
        proofOfIdentity: frontendIdentityType || existingApplicants[idx]?.proofOfIdentity || "Driver's License",
        idValue: data.idValue || existingApplicants[idx]?.idValue || "",
        phoneNumber: existingApplicants[idx]?.phoneNumber || "",
        email: existingApplicants[idx]?.email || "",
        addressLine2: existingApplicants[idx]?.addressLine2 || "",
        isIncomplete: data.isIncomplete && idx < 0,
        references: references,
    };

    const updatedApplicants = [...existingApplicants];
    if (idx >= 0) {
        updatedApplicants[idx] = nextApplicant as (typeof updatedApplicants)[number];
    } else {
        updatedApplicants.push(nextApplicant as (typeof updatedApplicants)[number]);
    }
    setFieldValue("applicants", updatedApplicants);

    return applicantId;
};

export { handleIdentityDocument };
